import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import InputBase from '@material-ui/core/InputBase';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import searchAndSort from 'features/POSRegisters/POSLanding/functions/searchAndSort';
import SearchSuggestions from './components/SearchSuggestions';
import {ClickAwayListener} from '@material-ui/core';
import {debounce} from 'lodash';
import {usePOSState} from 'features/POSRegisters/components/POSState';
import {useHistory, useLocation} from 'react-router-dom';
import {useSiteState} from 'hooks/useSiteInfo/SiteStateProvider';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';
import SiteType from 'hooks/useSiteInfo/interfaces/SiteType';

const StyledSiteSearchContainer = styled.div`
  height: 48px;
  &.visible {
    display: block;
  }
  &.hidden {
    display: none;
  }
`;

const StyledInputContainer = styled.div`
  background-color: ${props => props.theme.text}1F;
  border-radius: 8px;
  display: grid;
  grid-template-areas: 'search input clear';
  grid-template-columns: 48px auto 48px;
  &.search-active {
    border-radius: 8px 8px 0px 0px;
  }
`;

const StyledInputBase = styled(InputBase)`
  grid-area: input;
  color: ${props => props.theme.text};
  width: 182px;
  transition: width 200ms;
  &.search-active {
    width: 300px;
  }
`;

const StyledIconButton = styled(IconButton)`
  color: ${props => props.theme.text};
  &.Mui-disabled {
    opacity: 0;
  }
  &.search-icon {
    grid-area: search;
  }
  &.clear-icon {
    grid-area: clear;
  }
`;

function useQuery() {
  const {search} = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function SiteSearch() {
  const history = useHistory();
  let query = useQuery();
  const viewAllQuery: string | null = query.get('viewAll');
  const siteIdParam: string | null = query.get('siteId');
  const isSiteSearchVisible = siteIdParam !== null || viewAllQuery !== null;

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredSearchValue, setFilteredSearchValue] = useState<string>('');
  const [suggestedSearchItems, setSuggestedSearchItems] = useState<Site[]>([]);
  const {dispatch: reactDispatch} = usePOSState();
  const {
    state: {sites, isSiteStateReady},
  } = useSiteState();

  const allSiteSuggestions: Site[] = useMemo(() => {
    if (!isSiteStateReady) {
      return [];
    }
    return sites.filter(site => site.siteType === SiteType.Retail);
  }, [sites, isSiteStateReady]);

  const debouncedSetFilteredSearchValue = useCallback(
    debounce((searchValue: string) => {
      const filteredSearchValue = searchValue.toLowerCase();
      setFilteredSearchValue(filteredSearchValue);
    }, 150),
    []
  );

  useEffect(() => {
    debouncedSetFilteredSearchValue(searchValue);
  }, [searchValue, debouncedSetFilteredSearchValue]);

  useEffect(() => {
    if (isSiteStateReady && filteredSearchValue.length > 0 && allSiteSuggestions.length > 0) {
      const filteredSites = searchAndSort({
        filteredSearchValue,
        searchSuggestions: allSiteSuggestions,
        filterOptions: {},
      });
      setSuggestedSearchItems(filteredSites);
    } else {
      setSuggestedSearchItems([]);
    }
  }, [isSiteStateReady, allSiteSuggestions, filteredSearchValue]);

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setSearchValue(event.target.value);
  };

  const handleClear = () => {
    setSearchValue('');
  };

  const handleSiteSuggestionClick = (site: Site) => {
    reactDispatch({
      type: 'RESET_POS_DATA',
    });
    history.push(`/npos?siteId=${site.siteId}`);
    handleClear();
  };

  const handleSubmit = () => {
    history.push(`/npos?viewAll=${filteredSearchValue}`);
    handleClear();
  };

  const onKeyUp = (event: React.KeyboardEvent) => {
    const eventKey = event.key;
    const enterKey = 'Enter';
    if (eventKey === enterKey) {
      handleSubmit();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <StyledSiteSearchContainer
        id="npos-header-search-container"
        className={isSiteSearchVisible ? 'visible' : 'hidden'}
      >
        <StyledInputContainer
          className={isOpen && suggestedSearchItems.length > 0 ? 'search-active' : ''}
        >
          <StyledIconButton
            type="submit"
            onClick={handleSubmit}
            disabled={!isSiteStateReady}
            aria-label="search"
            className={'search-icon'}
          >
            <SearchIcon />
          </StyledIconButton>
          <StyledInputBase
            placeholder="Search"
            type="text"
            autoComplete={'off'}
            inputProps={{'aria-label': 'Search Nike Point of Sales Sites'}}
            disabled={!isSiteStateReady}
            value={searchValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyUp={onKeyUp}
            className={isOpen ? 'search-active' : ''}
          />
          <StyledIconButton
            type="button"
            disabled={searchValue.length < 1}
            aria-label="clear"
            onClick={handleClear}
            className={'clear-icon'}
          >
            <ClearIcon />
          </StyledIconButton>
        </StyledInputContainer>
        <SearchSuggestions
          isOpen={isOpen}
          suggestedSearchItems={suggestedSearchItems}
          handleSiteSuggestionClick={handleSiteSuggestionClick}
        />
      </StyledSiteSearchContainer>
    </ClickAwayListener>
  );
}
